import { Center, Text, Text3D, useTexture } from '@react-three/drei'
import { Select } from '@react-three/postprocessing'
import { createElement, useMemo, useRef, useState } from 'react'
import { MathUtils, MeshMatcapMaterial, type Group, type Texture } from 'three'

import { SphereHitbox } from '@components/helpers'
import {
  WagonContainerBlueModel,
  WagonContainerGreenModel,
  WagonFlatModel,
} from '@components/models'

import { useIsTouch, useVehicleKinematics } from '@hooks'
import { GamePhase, useGameStore } from '@stores'
import { RAIL_HEIGHT } from '@utils'

interface LetterConfig {
  letter: string
  rotation: number
}

const LETTERS_MAP: Record<string, LetterConfig> = {
  NPC_W1: { letter: 'A', rotation: 90 },

  NPC_W3: { letter: 'L', rotation: 90 },
  NPC_W4: { letter: 'I', rotation: 90 },
  NPC_W5: { letter: 'T', rotation: 90 },
  NPC_W6: { letter: 'T', rotation: 90 },
  NPC_W7: { letter: 'L', rotation: 90 },
  NPC_W8: { letter: 'E', rotation: 90 },

  NPC_W16: { letter: 'L', rotation: -90 },
  NPC_W15: { letter: 'O', rotation: -90 },
  NPC_W14: { letter: 'C', rotation: -90 },
  NPC_W13: { letter: 'O', rotation: -90 },
}

interface WagonProps {
  id: string
}

const BUTTON_TEXT_MATERIAL = new MeshMatcapMaterial({ color: 'white' })

const LETTER_TEXT_MATERIALS = new WeakMap<Texture, MeshMatcapMaterial>()

function getLetterTextMaterial(matcap: Texture) {
  let material = LETTER_TEXT_MATERIALS.get(matcap)

  if (!material) {
    material = new MeshMatcapMaterial({ color: 'orange', matcap })
    LETTER_TEXT_MATERIALS.set(matcap, material)
  }

  return material
}

export function TitleWagon({ id }: WagonProps) {
  const isTouch = useIsTouch()

  const ref = useRef<Group>(null)
  useVehicleKinematics(ref, id)

  const phase = useGameStore(state => state.phase)
  const setPhase = useGameStore(state => state.setPhase)
  const setLevelComplete = useGameStore(state => state.setLevelComplete)
  const [highlight, setHighlight] = useState(false)
  const matcap = useTexture('/textures/matcap.png')

  const startButton = id === 'NPC_W11'
  const creditsButton = id === 'NPC_W10'
  const isButton = startButton || creditsButton

  const autoHighlight = isButton && isTouch && phase < GamePhase.TITLE_TRANSITION

  const material = useMemo(
    () => (isButton ? BUTTON_TEXT_MATERIAL : getLetterTextMaterial(matcap)),
    [isButton, matcap],
  )

  const Model = useMemo(
    () =>
      startButton
        ? WagonContainerGreenModel
        : creditsButton
          ? WagonContainerBlueModel
          : WagonFlatModel,
    [startButton, creditsButton],
  )

  return (
    <group ref={ref}>
      {isButton && (
        <SphereHitbox
          position-y={RAIL_HEIGHT + 0.31}
          radius={0.5}
          onPointerOver={() => setHighlight(true)}
          onPointerOut={() => setHighlight(false)}
          onClick={event => {
            event.stopPropagation()
            setHighlight(false)

            if (phase >= GamePhase.TITLE_TRANSITION) return

            if (startButton) {
              setLevelComplete(true)
            } else {
              setPhase(GamePhase.CREDITS)
            }
          }}
        />
      )}

      <group position-y={RAIL_HEIGHT + 0.03}>
        {createElement(Model, {
          scale: [0.4, 0.35, 0.3],
          highlight: (highlight || autoHighlight) && phase < GamePhase.TITLE_TRANSITION,
        })}
      </group>

      {LETTERS_MAP[id] && (
        <Center position-y={0.7}>
          <Text3D
            castShadow
            receiveShadow
            font="/fonts/jaro.json"
            material={material!}
            rotation-y={MathUtils.degToRad(LETTERS_MAP[id].rotation)}
            curveSegments={1}
          >
            {LETTERS_MAP[id].letter}
          </Text3D>
        </Center>
      )}

      {isButton && (
        <Select enabled={(highlight || autoHighlight) && phase < GamePhase.TITLE_TRANSITION}>
          <Center position-y={0.6}>
            <Text3D
              receiveShadow
              scale={0.5}
              scale-y={creditsButton ? 0.4 : 0.5}
              curveSegments={1}
              font="/fonts/jaro.json"
              material={material!}
              rotation-x={MathUtils.degToRad(-90)}
              rotation-z={MathUtils.degToRad(-90)}
            >
              {startButton ? '▶' : 'i'}
            </Text3D>
          </Center>
          <Text
            font="/fonts/jaro.ttf"
            scale={0.2}
            rotation-y={MathUtils.degToRad(-90)}
            position={[-0.21, 0.4, 0]}
          >
            {startButton ? 'START' : 'CREDITS'}
          </Text>
        </Select>
      )}
    </group>
  )
}

useTexture.preload('/textures/matcap.png')
